'use client'

import { useEffect, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { testInputsFromFields, type TestInputField } from '@/lib/commands/test-input-schema'
import type { TraceSummary } from '@/lib/commands/trace'
import { CommandRunAttachments } from './CommandRunAttachments'
import { SandboxToggle } from './SandboxToggle'
import { TraceStep } from './TraceStep'

type TestResult = {
  response?: string
  error?: string
  duration_ms?: number
  sandbox?: boolean
  trace?: TraceSummary
  estimated_cost_cents?: number
  estimated_cost_dollars?: string
}

export function TestPanel({
  commandId,
  testFields,
  onClose,
  onRunComplete,
}: {
  commandId: string
  testFields: TestInputField[]
  onClose: () => void
  onRunComplete?: () => void
}) {
  const [sandbox, setSandbox] = useState(true)
  const [testing, setTesting] = useState(false)
  const [testValues, setTestValues] = useState<Record<string, string>>({})
  const [testResult, setTestResult] = useState<TestResult | null>(null)
  const [attachments, setAttachments] = useState<Array<{ id: string; file_name: string; file_type: string; file_size: number }>>([])

  useEffect(() => {
    setTestValues(Object.fromEntries(testFields.map((field) => [field.key, field.defaultValue])))
    setTestResult(null)
  }, [testFields])

  const summary = useMemo(() => {
    if (!testResult) return null
    const tokens = testResult.trace?.tokensUsed
    const tokenTotal = (tokens?.input ?? 0) + (tokens?.output ?? 0)
    const costCents = testResult.trace?.costCents ?? 0
    const seconds = ((testResult.duration_ms ?? 0) / 1000).toFixed(1)
    return `${tokenTotal.toLocaleString()} tokens · $${(costCents / 100).toFixed(3)} · ${seconds}s${testResult.sandbox ? ' · sandbox' : ''}`
  }, [testResult])

  const traceSteps = testResult?.trace?.steps ?? []
  const hasActivity = traceSteps.some((step) => step.kind === 'tool' || step.kind === 'dryrun' || step.kind === 'ai')

  const runTest = async () => {
    const inputs = testInputsFromFields(testFields, testValues)
    setTesting(true)
    setTestResult(null)
    const res = await fetch(`/api/commands/${commandId}/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ inputs, sandbox, attachment_ids: attachments.map((attachment) => attachment.id) }),
    })
    const data = await res.json().catch(() => ({}))
    setTesting(false)
    setTestResult({
      response: String((data as { response?: unknown }).response || ''),
      error: typeof (data as { error?: unknown }).error === 'string' ? (data as { error: string }).error : undefined,
      duration_ms: typeof (data as { duration_ms?: unknown }).duration_ms === 'number' ? (data as { duration_ms: number }).duration_ms : undefined,
      sandbox: (data as { sandbox?: unknown }).sandbox === true,
      trace: (data as { trace?: TraceSummary }).trace,
      estimated_cost_cents: typeof (data as { estimated_cost_cents?: unknown }).estimated_cost_cents === 'number'
        ? (data as { estimated_cost_cents: number }).estimated_cost_cents
        : undefined,
      ['estimated_cost_dollars']: typeof (data as { estimated_cost_dollars?: unknown }).estimated_cost_dollars === 'string'
        ? (data as { estimated_cost_dollars: string }).estimated_cost_dollars
        : undefined,
    })
    onRunComplete?.()
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-atrium-background text-atrium-black">
      <div className="shrink-0 border-b border-atrium-border px-6 py-4">
        <h3 className="text-base font-semibold">Test command</h3>
        <p className="mt-0.5 text-sm text-atrium-black/60">Run with sample inputs to preview the output.</p>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        <div className="space-y-5">
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-atrium-black">
              Inputs {testFields.length === 0 ? <span className="font-normal text-atrium-black/50">(none required for this command)</span> : null}
            </h4>
            {testFields.map((field) => (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={`test-${field.key}`}>{field.label}</Label>
                {field.type === 'select' && field.options ? (
                  <Select
                    value={testValues[field.key] ?? field.defaultValue}
                    onValueChange={(value) => setTestValues((prev) => ({ ...prev, [field.key]: value }))}
                  >
                    <SelectTrigger id={`test-${field.key}`} className="w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {field.options.map((option) => <SelectItem key={option} value={option}>{labelize(option)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id={`test-${field.key}`}
                    value={testValues[field.key] ?? field.defaultValue}
                    onChange={(e) => setTestValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  />
                )}
              </div>
            ))}
          </div>

          <SandboxToggle checked={sandbox} onCheckedChange={setSandbox} />

          <CommandRunAttachments attachments={attachments} onChange={setAttachments} />

          <Button type="button" onClick={() => void runTest()} disabled={testing}>
            {testing ? 'Running...' : 'Run test'}
          </Button>

          {testResult && (
            <div className="space-y-5 border-t border-atrium-border pt-5">
              <div>
                <p className={testResult.error ? 'text-sm font-medium text-red-700' : 'text-sm font-medium text-green-700'}>
                  {testResult.error ? 'Run failed' : `Run completed in ${((testResult.duration_ms ?? 0) / 1000).toFixed(1)}s${testResult.sandbox ? ' · sandbox' : ''}`}
                </p>
                {summary && <p className="mt-1 text-xs text-atrium-black/55">{summary}</p>}
              </div>

              {traceSteps.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-atrium-black">Steps</h4>
                  <div className="space-y-2">
                    {traceSteps.map((step, index) => (
                      <TraceStep key={`${step.kind}-${index}`} step={step} index={index} />
                    ))}
                  </div>
                </div>
              )}

              {!hasActivity && !testResult.error && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  This command doesn&apos;t do anything yet.
                </div>
              )}

              <div className="space-y-2">
                <h4 className="text-sm font-medium text-atrium-black">{testResult.error ? 'Error' : 'Final output'}</h4>
                <div className="rounded-lg border border-atrium-border bg-white p-4">
                  {testResult.error ? (
                    <p className="whitespace-pre-wrap text-sm text-red-700">{testResult.error}</p>
                  ) : (
                    <div className="prose prose-sm max-w-none text-atrium-black">
                      <ReactMarkdown>{testResult.response || ''}</ReactMarkdown>
                    </div>
                  )}
                </div>
                {!testResult.error && testResult.sandbox && testResult.estimated_cost_dollars && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="mt-2 inline-flex cursor-help text-xs font-medium text-atrium-black/65">
                        Estimated cost: {testResult.estimated_cost_dollars}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      AI cost only; write-scoped tools are mocked and not billed
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex shrink-0 justify-end gap-2 border-t border-atrium-border bg-atrium-background px-6 py-4">
        <Button type="button" variant="outline" onClick={onClose}>Close</Button>
        <Button type="button" onClick={() => void runTest()} disabled={testing}>
          {testing ? 'Running...' : 'Run test'}
        </Button>
      </div>
    </div>
  )
}

function labelize(value: string) {
  return value.replace(/[._]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}
